import { inject, Injectable, InjectionToken } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { MethodTypeListEffects } from '../../../effects/method-type-list.effects';
import { MethodTypeEditorEffects } from '../../../effects/method-type-editor.effects';
import { MethodTypeItem, MethodTypeListState } from '../../../state/method-type-list.state';
import { MethodTypeEditorState, MethodTypeDraft } from '../../../state/method-type-editor.state';
import {
  AssignedMethodTypesState,
  MethodTypeManagerContext,
} from '../../../state/assigned-method-types.state';
import {
  StagedMethodTypeDraft,
  StagedMethodTypeOverride,
  StagedMethodTypesState,
} from '../../../state/staged-method-types.state';
import { MethodTypeListItem } from '../method-types-list.component';

/**
 * The case type this manager assigns method types to. Provided per mount (the
 * case-type id + whether it is already persisted). A staged (not-yet-persisted)
 * case type has no persisted associations to seed, so its assigned list is
 * staged-only.
 */
export const METHOD_TYPE_MANAGER_CONTEXT = new InjectionToken<MethodTypeManagerContext>('METHOD_TYPE_MANAGER_CONTEXT');

/**
 * Orchestrates the two-list method-type manager (mirror of
 * `MethodPipelineManagerFacade`): the left list is the global method-type
 * catalog (`MethodTypeListEffects`, with staged drafts merged in), the right
 * list is the method types assigned to the case type (the consumer-seeded
 * `AssignedMethodTypesState`). The "NEW METHOD TYPE" / edit dialog choreography
 * seeds the shared method-type editor and stages the produced draft.
 *
 * The assigned (right) list is NOT paginated and has no query/effects — it is
 * seeded by the consumer (`AssignedMethodTypesState`, ResourceManager parity).
 */
@Injectable()
export class MethodTypeManagerFacade {
  private readonly context = inject<MethodTypeManagerContext>(METHOD_TYPE_MANAGER_CONTEXT);

  constructor(
    private readonly listEffects: MethodTypeListEffects,
    private readonly listState: MethodTypeListState,
    private readonly stagedMethodTypes: StagedMethodTypesState,
    private readonly assignedState: AssignedMethodTypesState,
    private readonly editorEffects: MethodTypeEditorEffects,
    private readonly editorState: MethodTypeEditorState
  ) {}

  // ============================================================================
  // Left list — global method-type catalog (staged drafts merged in by the
  // list effects); each row's `assigned` flag derived from the right list.
  // ============================================================================

  public readonly availableMethodTypes$: Observable<MethodTypeListItem[]> = combineLatest([
    this.listEffects.methodTypes$,
    this.assignedState.assignedIds$,
  ]).pipe(
    map(([items, assignedIds]) =>
      items.map((item: MethodTypeItem) => ({ ...this.toListItem(item), assigned: assignedIds.has(item.id) }))
    )
  );

  public readonly searchTerm$ = this.listEffects.searchTerm$;
  public readonly totalCount$ = this.listEffects.totalCount$;
  public readonly pageSize$ = this.listEffects.pageSize$;
  public readonly nextMethodTypeToken$ = this.listEffects.nextMethodTypeToken$;
  public readonly previousMethodTypeTokens$ = this.listEffects.previousMethodTypeTokens$;
  public readonly noMethodTypes$ = this.listEffects.noMethodTypes$;
  public readonly counting$ = this.listEffects.counting$;
  public readonly loading$ = combineLatest([
    this.listEffects.loading$,
    this.listEffects.searching$,
    this.listEffects.counting$,
  ]).pipe(map(([loading, searching, counting]) => loading || searching || counting));

  // ============================================================================
  // Right list — method types assigned to the case type (no pagination/search).
  // Every row is assigned, so the toggle un-assigns.
  // ============================================================================

  public readonly assignedMethodTypes$: Observable<MethodTypeListItem[]> = this.assignedState.combinedAssigned$.pipe(
    map((items) => items.map((item) => ({ ...this.toListItem(item), assigned: true })))
  );

  public readonly assignedNoMethodTypes$: Observable<boolean> = this.assignedMethodTypes$.pipe(
    map((items) => items.length === 0),
    distinctUntilChanged()
  );

  // ============================================================================
  // Commands
  // ============================================================================

  /** Record the context (right list seeded separately by the consumer) and load the catalog. */
  public readonly initialize = this.listState.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.assignedState.setContext(this.context);
        this.listEffects.initializeMethodTypes();
      })
    )
  );

  /** Catalog search (alias of the list state's updater; triggers the reload). */
  public readonly search = this.listEffects.setSearchTerm;

  /** Catalog pagination. */
  public readonly loadMore = this.listEffects.loadMoreMethodTypes;

  /** Toggle a left-list (catalog) row: un-assign if assigned, assign otherwise. */
  public readonly toggleAvailable = this.assignedState.effect((item$: Observable<MethodTypeListItem>) =>
    item$.pipe(
      tap((item: MethodTypeListItem) => {
        if (item.assigned) {
          this.assignedState.unassign(item.id);
        } else {
          this.assignedState.assign(this.toMethodTypeItem(item));
        }
      })
    )
  );

  /** Un-assign a right-list row. */
  public readonly unassign = this.assignedState.effect((item$: Observable<MethodTypeListItem>) =>
    item$.pipe(tap((item: MethodTypeListItem) => this.assignedState.unassign(item.id)))
  );

  // ============================================================================
  // Editor-dialog choreography
  // ============================================================================

  /** Original (pre-edit) catalog summary of the method type being edited. */
  private readonly _editOriginal$ = new BehaviorSubject<MethodTypeItem | null>(null);

  /**
   * Seed the editor for a brand-new method type before opening the dialog. The
   * caller mints the placeholder id (`tmp-<uuid>`) and passes it in so the SAME
   * id drives both this create session AND the dialog's embedded
   * pipeline/resource manager contexts (the editor dialog provides those from
   * the id it is opened with — see `MethodTypeEditorDialogComponent`).
   */
  public readonly beginCreateMethodType = this.editorState.effect((methodTypeId$: Observable<string>) =>
    methodTypeId$.pipe(
      tap((methodTypeId: string) => {
        this._editOriginal$.next(null);
        this.editorEffects.loadMethodType({ methodTypeId, persisted: false });
      })
    )
  );

  /**
   * Seed the editor for an existing method type. If it already has a staged
   * draft from this session (created or edited earlier), seed from that draft so
   * the user sees their staged edits; otherwise load the persisted detail.
   */
  public readonly beginEditMethodType = this.editorState.effect((item$: Observable<MethodTypeListItem>) =>
    item$.pipe(
      concatLatestFrom(() => [this.stagedMethodTypes.pendingMethodTypes$, this.stagedMethodTypes.methodTypeOverrides$]),
      tap(
        ([item, pending, overrides]: [
          MethodTypeListItem,
          StagedMethodTypeDraft[],
          Record<string, StagedMethodTypeOverride>
        ]) => {
          this._editOriginal$.next(this.toMethodTypeItem(item));
          const override = overrides[item.id];
          const stagedDraft = pending.find((d) => d.id === item.id) ?? override?.draft;
          if (stagedDraft) {
            // Routed through the effects so the dirty-tracking baseline is
            // captured alongside the seed (staged edits are not new work).
            this.editorEffects.seedFromDraft({ ...stagedDraft, persisted: !!override });
          } else {
            this.editorEffects.loadMethodType({ methodTypeId: item.id, persisted: true });
          }
        }
      )
    )
  );

  /**
   * Apply the editor's produced draft on `saved`. Mirrors
   * `MethodPipelineManagerFacade.applySavedDraft`, branching on the editor's
   * `persisted` flag and whether the id is a still-pending catalog row:
   * - new method type (tmp id, not pending yet) → pending catalog row + auto-assign;
   * - re-edit of a still-pending method type → upsert pending in place + patch assigned;
   * - edit of a persisted method type → catalog override + patch assigned.
   */
  public readonly stageSavedMethodType = this.listState.effect<void>((trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => [
        this.editorState.draft$,
        this.editorState.assetTypeName$,
        this.editorState.persisted$,
        this._editOriginal$,
        this.stagedMethodTypes.pendingMethodTypes$,
      ]),
      tap(
        ([, draft, assetTypeName, persisted, original, pending]: [
          void,
          MethodTypeDraft,
          string | null,
          boolean,
          MethodTypeItem | null,
          StagedMethodTypeDraft[]
        ]) => this.applySavedDraft(draft, assetTypeName, persisted, original, pending)
      )
    )
  );

  // ============================================================================
  // Helpers
  // ============================================================================

  private applySavedDraft(
    draft: MethodTypeDraft,
    assetTypeName: string | null,
    persisted: boolean,
    original: MethodTypeItem | null,
    pending: StagedMethodTypeDraft[]
  ): void {
    const id = draft.id;
    if (!id) return; // a loaded editor session always carries an id (tmp or real)

    const stagedDraft: StagedMethodTypeDraft = {
      id,
      name: draft.name,
      assetTypeId: draft.assetTypeId,
      assetTypeName: assetTypeName ?? undefined,
      sampleNames: draft.sampleNames,
      config: draft.config,
      acquisitionFields: draft.acquisitionFields,
    };
    const summary: MethodTypeItem = {
      id,
      name: draft.name,
      type: undefined,
      usedByCaseTypes: undefined,
      usedBySamples: undefined,
      pending: persisted ? undefined : true,
    };

    if (!persisted) {
      this.stagedMethodTypes.stagePending(stagedDraft);
      if (this.isPending(id, pending)) {
        // Re-edit of a not-yet-persisted method type: patch the assigned row.
        this.assignedState.updateAssigned(summary);
      } else {
        // Brand-new method type: pending catalog row, auto-assigned.
        this.assignedState.assign(summary);
      }
    } else {
      // Edit of a persisted method type: catalog override + patch the assigned row.
      const originalName = original?.name ?? draft.name;
      this.stagedMethodTypes.stageOverride({ original: { id, name: originalName }, draft: stagedDraft });
      this.assignedState.updateAssigned(summary);
    }

    // Reflect the staged catalog change (pending row / override) in the visible page.
    this.listEffects.relistPage();
    this.listEffects.countItems();
    this._editOriginal$.next(null);
  }

  private isPending(id: string, pending: StagedMethodTypeDraft[]): boolean {
    return pending.some((p) => p.id === id);
  }

  private toListItem(item: MethodTypeItem): MethodTypeListItem {
    return {
      id: item.id,
      name: item.name,
      usedByCaseTypes: item.usedByCaseTypes,
      usedBySamples: item.usedBySamples,
      pending: item.pending,
    };
  }

  private toMethodTypeItem(item: MethodTypeListItem): MethodTypeItem {
    return {
      id: item.id,
      name: item.name,
      type: undefined,
      usedByCaseTypes: item.usedByCaseTypes,
      usedBySamples: item.usedBySamples,
      pending: item.pending,
    };
  }
}
